import { useState, useEffect } from 'react';
import { supabase } from '../supabase';

const GOLD = '#FFC220';

const emptyForm = {
  quote: '',
  name: '',
  label: 'Student',
  sort_order: 1,
  active: true,
};

export default function Testimonials() {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const fetchTestimonials = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('testimonials')
      .select('*')
      .order('sort_order', { ascending: true });
    if (!error) setTestimonials(data);
    setLoading(false);
  };

  const handleEdit = (t) => {
    setForm({
      quote: t.quote || '',
      name: t.name || '',
      label: t.label || 'Student',
      sort_order: t.sort_order || 1,
      active: t.active,
    });
    setEditingId(t.id);
    setShowForm(true);
    window.scrollTo(0, 0);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this testimonial?')) return;
    await supabase.from('testimonials').delete().eq('id', id);
    fetchTestimonials();
  };

  const handleToggle = async (t) => {
    await supabase.from('testimonials').update({ active: !t.active }).eq('id', t.id);
    fetchTestimonials();
  };

  const handleMoveUp = async (t, index) => {
    if (index === 0) return;
    const above = testimonials[index - 1];
    await supabase.from('testimonials').update({ sort_order: above.sort_order }).eq('id', t.id);
    await supabase.from('testimonials').update({ sort_order: t.sort_order }).eq('id', above.id);
    fetchTestimonials();
  };

  const handleMoveDown = async (t, index) => {
    if (index === testimonials.length - 1) return;
    const below = testimonials[index + 1];
    await supabase.from('testimonials').update({ sort_order: below.sort_order }).eq('id', t.id);
    await supabase.from('testimonials').update({ sort_order: t.sort_order }).eq('id', below.id);
    fetchTestimonials();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.quote || !form.name) {
      alert('Please enter a quote and name.');
      return;
    }
    setSaving(true);
    const payload = {
      quote: form.quote,
      name: form.name,
      label: form.label,
      sort_order: parseInt(form.sort_order) || 1,
      active: form.active,
    };

    if (editingId) {
      await supabase.from('testimonials').update(payload).eq('id', editingId);
      setSuccessMsg('Testimonial updated!');
    } else {
      await supabase.from('testimonials').insert(payload);
      setSuccessMsg('Testimonial added!');
    }

    setTimeout(() => setSuccessMsg(''), 3000);
    setForm(emptyForm);
    setEditingId(null);
    setShowForm(false);
    setSaving(false);
    fetchTestimonials();
  };

  const handleCancel = () => {
    setForm(emptyForm);
    setEditingId(null);
    setShowForm(false);
  };

  if (loading) return (
    <div style={styles.loading}><p style={{ color: GOLD }}>Loading...</p></div>
  );

  return (
    <div style={styles.wrap}>

      {/* TOPBAR */}
      <div style={styles.topbar}>
        <h1 style={styles.topbarTitle}>
          Testimonials
          <span style={styles.topbarCount}>{testimonials.length} testimonials</span>
        </h1>
        <button style={styles.topbarBtn} onClick={() => { setShowForm(!showForm); setEditingId(null); setForm(emptyForm); }}>
          {showForm && !editingId ? '✕ Cancel' : '+ Add Testimonial'}
        </button>
      </div>

      <div style={styles.content}>

        {successMsg && <div style={styles.successBanner}>✅ {successMsg}</div>}

        {/* FORM */}
        {showForm && (
          <div style={styles.formCard}>
            <h2 style={styles.formTitle}>{editingId ? 'Edit Testimonial' : 'Add New Testimonial'}</h2>
            <form onSubmit={handleSubmit}>

              <div style={styles.formRow}>
                <label style={styles.label}>QUOTE *</label>
                <textarea
                  style={styles.textarea}
                  rows={4}
                  placeholder="Enter the testimonial quote..."
                  value={form.quote}
                  onChange={(e) => setForm({ ...form, quote: e.target.value })}
                  required
                />
              </div>

              <div style={styles.formGrid}>
                <div style={styles.formRow}>
                  <label style={styles.label}>NAME *</label>
                  <input
                    style={styles.input}
                    type="text"
                    placeholder="e.g. Sarah M."
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    required
                  />
                </div>
                <div style={styles.formRow}>
                  <label style={styles.label}>LABEL</label>
                  <select
                    style={styles.select}
                    value={form.label}
                    onChange={(e) => setForm({ ...form, label: e.target.value })}
                  >
                    <option value="Student">Student</option>
                    <option value="Parent">Parent</option>
                  </select>
                </div>
              </div>

              <div style={styles.formGrid}>
                <div style={styles.formRow}>
                  <label style={styles.label}>SORT ORDER</label>
                  <input
                    style={styles.input}
                    type="number"
                    min="1"
                    value={form.sort_order}
                    onChange={(e) => setForm({ ...form, sort_order: e.target.value })}
                  />
                </div>
                <div style={styles.formRow}>
                  <label style={styles.label}>STATUS</label>
                  <label style={styles.checkLabel}>
                    <input
                      type="checkbox"
                      checked={form.active}
                      onChange={(e) => setForm({ ...form, active: e.target.checked })}
                      style={{ marginRight: '8px' }}
                    />
                    Active — show on website
                  </label>
                </div>
              </div>

              <div style={styles.formBtns}>
                <button type="submit" style={styles.submitBtn} disabled={saving}>
                  {saving ? 'Saving...' : editingId ? 'Save Changes' : 'Add Testimonial'}
                </button>
                <button type="button" style={styles.cancelBtn} onClick={handleCancel}>Cancel</button>
              </div>

            </form>
          </div>
        )}

        {/* TESTIMONIALS LIST */}
        {testimonials.length === 0 ? (
          <div style={styles.empty}>No testimonials yet. Click "+ Add Testimonial" to add your first one.</div>
        ) : (
          <div style={styles.list}>
            {testimonials.map((t, index) => (
              <div key={t.id} style={{ ...styles.row, ...(t.active ? {} : styles.rowInactive) }}>
                <div style={styles.rowOrder}>{t.sort_order}</div>
                <div style={styles.rowContent}>
                  <div style={styles.rowQuote}>"{t.quote}"</div>
                  <div style={styles.rowMeta}>
                    <span style={styles.rowName}>— {t.name}</span>
                    <span style={{
                      ...styles.rowLabel,
                      background: t.label === 'Parent' ? 'rgba(59,130,246,0.1)' : 'rgba(255,194,32,0.1)',
                      color: t.label === 'Parent' ? '#1d4ed8' : '#b8860b',
                    }}>{t.label}</span>
                  </div>
                </div>
                <div style={styles.rowActions}>
                  <div style={styles.reorderBtns}>
                    <button style={styles.arrowBtn} onClick={() => handleMoveUp(t, index)} disabled={index === 0}>↑</button>
                    <button style={styles.arrowBtn} onClick={() => handleMoveDown(t, index)} disabled={index === testimonials.length - 1}>↓</button>
                  </div>
                  <Toggle value={t.active} onChange={() => handleToggle(t)} />
                  <button style={styles.editBtn} onClick={() => handleEdit(t)}>Edit</button>
                  <button style={styles.deleteBtn} onClick={() => handleDelete(t.id)}>Delete</button>
                </div>
              </div>
            ))}
          </div>
        )}

      </div>
    </div>
  );
}

function Toggle({ value, onChange }) {
  return (
    <div onClick={onChange} style={{ width: '36px', height: '20px', borderRadius: '10px', background: value ? GOLD : '#ddd', position: 'relative', cursor: 'pointer', transition: 'background 0.2s', flexShrink: 0 }}>
      <div style={{ position: 'absolute', width: '14px', height: '14px', borderRadius: '50%', background: '#fff', top: '3px', left: value ? '19px' : '3px', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
    </div>
  );
}

const styles = {
  wrap: { display: 'flex', flexDirection: 'column', minHeight: '100vh' },
  loading: { display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' },
  topbar: { background: '#fff', borderBottom: '1px solid #e0e0e0', padding: '16px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, zIndex: 10 },
  topbarTitle: { fontSize: '18px', fontWeight: '500', color: '#111', margin: 0, display: 'flex', alignItems: 'center', gap: '10px' },
  topbarCount: { fontSize: '13px', fontWeight: '400', color: '#aaa' },
  topbarBtn: { background: GOLD, color: '#111', border: 'none', borderRadius: '8px', padding: '8px 16px', fontSize: '13px', fontWeight: '500', cursor: 'pointer' },
  content: { padding: '24px', flex: 1 },
  successBanner: { background: '#f0fff4', border: '1px solid #86efac', borderRadius: '10px', padding: '12px 16px', marginBottom: '20px', color: '#166534', fontSize: '14px' },
  formCard: { background: '#fff', border: '1px solid #e0e0e0', borderRadius: '14px', padding: '24px', marginBottom: '24px' },
  formTitle: { fontSize: '16px', fontWeight: '500', color: '#111', marginBottom: '20px' },
  formGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' },
  formRow: { marginBottom: '16px' },
  label: { display: 'block', color: '#888', fontSize: '10px', letterSpacing: '1px', marginBottom: '6px' },
  input: { width: '100%', padding: '10px 12px', border: '1px solid #e0e0e0', borderRadius: '8px', fontSize: '14px', color: '#111', fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' },
  textarea: { width: '100%', padding: '10px 12px', border: '1px solid #e0e0e0', borderRadius: '8px', fontSize: '13px', color: '#111', fontFamily: 'inherit', outline: 'none', resize: 'vertical', boxSizing: 'border-box' },
  select: { width: '100%', padding: '10px 12px', border: '1px solid #e0e0e0', borderRadius: '8px', fontSize: '14px', color: '#111', fontFamily: 'inherit', outline: 'none', background: '#fff', cursor: 'pointer', boxSizing: 'border-box' },
  checkLabel: { display: 'flex', alignItems: 'center', fontSize: '14px', color: '#444', cursor: 'pointer', marginTop: '8px' },
  formBtns: { display: 'flex', gap: '12px', marginTop: '8px' },
  submitBtn: { background: GOLD, color: '#111', border: 'none', borderRadius: '10px', padding: '11px 24px', fontSize: '14px', fontWeight: '500', cursor: 'pointer' },
  cancelBtn: { background: 'none', color: '#888', border: '1px solid #e0e0e0', borderRadius: '10px', padding: '11px 24px', fontSize: '14px', cursor: 'pointer' },
  list: { display: 'flex', flexDirection: 'column', gap: '8px' },
  row: { display: 'flex', alignItems: 'flex-start', gap: '14px', background: '#fff', border: '1px solid #e0e0e0', borderLeft: `4px solid ${GOLD}`, borderRadius: '0 12px 12px 0', padding: '14px 16px' },
  rowInactive: { opacity: 0.5, borderLeftColor: '#ddd' },
  rowOrder: { width: '28px', height: '28px', borderRadius: '50%', background: '#111', color: GOLD, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: '600', flexShrink: 0, marginTop: '2px' },
  rowContent: { flex: 1, minWidth: 0 },
  rowQuote: { fontSize: '14px', color: '#444', lineHeight: 1.6, marginBottom: '8px', fontStyle: 'italic' },
  rowMeta: { display: 'flex', alignItems: 'center', gap: '8px' },
  rowName: { fontSize: '13px', fontWeight: '500', color: '#111' },
  rowLabel: { fontSize: '11px', fontWeight: '500', padding: '2px 8px', borderRadius: '20px' },
  rowActions: { display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 },
  reorderBtns: { display: 'flex', flexDirection: 'column', gap: '2px' },
  arrowBtn: { background: '#f5f5f5', border: '1px solid #e0e0e0', borderRadius: '4px', width: '24px', height: '20px', fontSize: '12px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, color: '#666' },
  editBtn: { background: 'none', border: '1px solid #e0e0e0', borderRadius: '6px', padding: '4px 10px', fontSize: '12px', color: '#444', cursor: 'pointer' },
  deleteBtn: { background: 'none', border: '1px solid #e0e0e0', borderRadius: '6px', padding: '4px 10px', fontSize: '12px', color: '#ff6b6b', cursor: 'pointer' },
  empty: { padding: '32px', textAlign: 'center', color: '#888', fontSize: '13px', background: '#fff', border: '1px solid #e0e0e0', borderRadius: '12px' },
};
